// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckSquare, Square, FileSpreadsheet, Table2 } from 'lucide-react';
import './PeriodSelectorMulti.css';

const PeriodSelectorMulti = ({ 
  fileData, 
  periodOptions, 
  onProcess,
  onError, 
  onBack, 
  loading,
  isDemoUser = false,
  isAdminUser = false,
  hasExistingResults = false,
  onViewResults
}) => {
  const [selectedPeriods, setSelectedPeriods] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  // Auto-select all periods when component mounts or periodOptions change
  useEffect(() => {
    if (periodOptions && periodOptions.period_options) {
      const allPeriods = new Set(
        periodOptions.period_options.map(p => `${p.column_index}_${p.display_name}`)
      );
      setSelectedPeriods(allPeriods);
      setSelectAll(true);
    }
  }, [periodOptions]);

  const handlePeriodToggle = (period) => {
    const newSelected = new Set(selectedPeriods);
    const periodKey = `${period.column_index}_${period.display_name}`;
    
    if (newSelected.has(periodKey)) {
      newSelected.delete(periodKey);
    } else {
      newSelected.add(periodKey);
    }
    
    setSelectedPeriods(newSelected);
    setSelectAll(newSelected.size === periodOptions.period_options.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPeriods(new Set());
      setSelectAll(false);
    } else {
      const allPeriods = new Set(
        periodOptions.period_options.map(p => `${p.column_index}_${p.display_name}`)
      );
      setSelectedPeriods(allPeriods);
      setSelectAll(true);
    }
  };

  const getSelectedPeriodObjects = () => {
    return periodOptions.period_options.filter(period => 
      selectedPeriods.has(`${period.column_index}_${period.display_name}`)
    );
  };

  const handleProcess = async () => {
    if (selectedPeriods.size === 0) {
      onError('Please select at least one period to process');
      return;
    }
    
    const selectedPeriodObjects = getSelectedPeriodObjects();
    const effectiveDeterministic = true;
    const rowsToSend = undefined;
    const options = { forceValidation: !!isAdminUser };
    onProcess(selectedPeriodObjects, effectiveDeterministic, rowsToSend, options);
  };

  const totalPeriods = periodOptions.period_options.length;

  return (
    <div className="period-selector-card border-beam-container">
      <div className="border-beam"></div>
      
      {/* Compact Context Bar */}
      <div className="context-bar">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={16} />
          Back
        </button>
        
        <div className="context-meta">
          <div className="meta-chip file-chip">
            <FileSpreadsheet size={14} />
            <span className="chip-text">{fileData.file.name}</span>
          </div>
          <span className="meta-separator">/</span>
          <div className="meta-chip sheet-chip">
            <Table2 size={14} />
            <span className="chip-text">{fileData.sheetName}</span>
          </div>
        </div>

        <div className="selection-badge">
          <span className="selection-count">{selectedPeriods.size}</span>
          <span className="selection-separator">/</span>
          <span className="selection-total">{totalPeriods}</span>
        </div>
      </div>

      {/* Period Selection Section */}
      <div className="period-section">
        <div className="section-header">
          <h3 className="section-title">Select Periods</h3>
          <button 
            onClick={handleSelectAll}
            className={`select-all-btn ${selectAll ? 'active' : ''}`}
            disabled={loading}
          >
            {selectAll ? <CheckSquare size={15} /> : <Square size={15} />}
            {selectAll ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        <div className="period-table-wrapper">
          <table className="period-table">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Column</th>
                <th>Original Name</th>
              </tr>
            </thead>
            <tbody>
              {periodOptions.period_options.map((period) => {
                const periodKey = `${period.column_index}_${period.display_name}`;
                const isSelected = selectedPeriods.has(periodKey);
                const originalName = period.original_name || period.original || period.period_full_name || period.display_name;

                return (
                  <tr
                    key={period.column_index}
                    className={isSelected ? 'selected' : ''}
                    onClick={() => handlePeriodToggle(period)}
                  >
                    <td className="check-cell">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); handlePeriodToggle(period); }}
                      />
                    </td>
                    <td className="date-cell">{period.display_name}</td>
                    <td>Col {period.column_index}</td>
                    <td className="name-cell">{originalName}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Process Button */}
      <div className="action-footer">
        {hasExistingResults && onViewResults && (
          <button
            onClick={onViewResults}
            className="view-results-button"
            disabled={loading}
          >
            View Results
            <ArrowRight size={16} />
          </button>
        )}
        <button
          onClick={handleProcess}
          className="process-button"
          disabled={selectedPeriods.size === 0 || loading}
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              Processing {selectedPeriods.size} periods...
            </>
          ) : (
            <>
              Process {selectedPeriods.size} Period{selectedPeriods.size !== 1 ? 's' : ''}
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PeriodSelectorMulti;
